import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuerySample } from '../helpers';

export const dashboardTablesApi = createApi({
  reducerPath: 'RideShareDashboardTables',
  baseQuery: baseQuerySample,

  endpoints: (builder) => ({
    getMemberReportConfigs: builder.query({
      query: (tab?: string) => ({
        url: '/v1/search/reports/member/config',
        params: tab ? { tab } : undefined,
      }),
    }),
    getMemberReportConfig: builder.query({
      query: (reportId: string) => ({
        url: `/v1/search/reports/member/${reportId}/config`,
      }),
    }),
    getMemberReportData: builder.query({
      query: ({
        reportId,
        params = {},
      }: {
        reportId: string;
        params?: Record<string, any>;
      }) => ({
        url: `/v1/search/reports/member/${reportId}/data`,
        method: 'POST',
        params,
      }),
    }),
    getMemberReportAggregate: builder.query({
      query: ({
        reportId,
        params = {},
      }: {
        reportId: string;
        params?: Record<string, any>;
      }) => ({
        url: `/v1/search/reports/member/${reportId}/aggregate`,
        method: 'POST',
        params,
      }),
    }),
    getMemberReportDrillThrough: builder.query({
      query: ({
        params = {},
        memberIds = [],
      }: {
        params?: Record<string, any>;
        memberIds: number[];
      }) => ({
        url: '/v1/search/reports/member/drill-through/segments',
        method: 'POST',
        params,
        body: memberIds,
      }),
    }),
    getRideTableReportData: builder.query({
      query: (arg: any) => {
        const { body, ...params } = (arg ?? {}) as {
          body?: any;
          [key: string]: any;
        };

        return {
          url: `/v1/search/dashboard/ride/table/report-data`,
          method: 'POST',
          params,
          body: body ?? {},
        };
      },
    }),

    getDashboardIncompleteRideTable: builder.query({
      query: (params = {}) => ({
        url: `/v1/search/dashboard/ride/table/incomplete-ride`,
        method: 'POST',
        params,
        body: {},
      }),
    }),
    getDashboardRenderingService: builder.query({
      query: (params = {}) => ({
        url: `/v1/search/dashboard/ride/table/rendering-service`,
        method: 'POST',
        params,
        body: {},
      }),
    }),
    getDashboardOnTimePerformanceTable: builder.query({
      query: (params = {}) => ({
        url: `/v1/search/dashboard/ride/table/on-time-performance`,
        method: 'POST',
        params,
        body: {},
      }),
    }),

    getDashboardRideMemberTable: builder.query({
      query: (params) => ({
        url: `/v1/search/dashboard/ride/table/member`,
        method: 'POST',
        params,
        body: {},
      }),
    }),
    getDashboardLSIncompleteRide: builder.query({
      query: (params = {}) => ({
        url: `/v1/search/dashboard/ride/table/ls-incomplete-ride`,
        method: 'POST',
        params,
        body: {},
      }),
    }),
    getDashboardHeatMapTable: builder.query({
      query: (params = {}) => ({
        url: `/v1/search/dashboard/ride/table/heatmap`,
        method: 'POST',
        params,
        body: {},
      }),
    }),
    getDashboardRideTableStatus: builder.query({
      query: (params = {}) => ({
        url: `/v1/search/dashboard/ride/table/by-status`,
        method: 'POST',
        params,
        body: {},
      }),
    }),
    getDashboardRideTableByCounty: builder.query({
      query: (params = {}) => ({
        url: `/v1/search/dashboard/ride/table/by-county`,
        method: 'POST',
        params,
        body: {},
      }),
    }),
    getDashboardRideAverageMiles: builder.query({
      query: (params = {}) => ({
        url: `/v1/search/dashboard/ride/table/average-miles`,
        method: 'POST',
        params,
        body: {},
      }),
    }),
    getDashboardCostByCounty: builder.query({
      query: (params = {}) => ({
        url: `/v1/search/dashboard/ride/table/cost-by-county`,
        method: 'POST',
        params,
        body: {},
      }),
    }),
    getDashboardLSInterval: builder.query({
      query: (params) => ({
        url: `/v1/search/dashboard/ride/table/ls-otp-interval`,
        method: 'POST',
        params,
        body: {},
      }),
    }),
    getDashboardLSRoutine: builder.query({
      query: (params) => ({
        url: `/v1/search/dashboard/ride/table/ls-routine`,
        method: 'POST',
        params,
        body: {},
      }),
    }),
    getDashboardRideOtp: builder.query({
      query: (params = {}) => ({
        url: `/v1/search/dashboard/ride/table/otp-interval`,
        method: 'POST',
        params,
        body: {},
      }),
    }),
    getDashboardContractorTopCostMembers: builder.query({
      query: (arg: any) => {
        const { body, ...params } = (arg ?? {}) as {
          body?: any;
          [key: string]: any;
        };

        return {
          url: `/api/v2/search/contractor/top-cost/members`,
          method: 'POST',
          params,
          body: body ?? {},
        };
      },
    }),
    downloadExcel: builder.mutation({
      query: ({
        columns,
        filters = {},
        params,
        tableName,
      }: {
        columns: string[];
        params: any;
        tableName: string;
        filters?: Record<string, any>;
      }) => ({
        url: '/v1/search/dashboard/ride/table/export',
        method: 'POST',
        body: {
          filters,
          columns,
        },
        params: {
          ...params,
          table: tableName,
        },
        responseHandler: (response: any) => response.blob(),
      }),
    }),
  }),
});

export const {
  useGetMemberReportConfigsQuery,
  useGetMemberReportConfigQuery,
  useLazyGetMemberReportDataQuery,
  useLazyGetMemberReportAggregateQuery,
  useLazyGetMemberReportDrillThroughQuery,
  useLazyGetDashboardRideOtpQuery,
  useLazyGetDashboardLSRoutineQuery,
  useLazyGetDashboardLSIntervalQuery,
  useLazyGetDashboardCostByCountyQuery,
  useLazyGetDashboardRideAverageMilesQuery,
  useLazyGetDashboardContractorTopCostMembersQuery,
  useLazyGetDashboardRideTableByCountyQuery,
  useLazyGetDashboardRideTableStatusQuery,
  useLazyGetDashboardHeatMapTableQuery,
  useLazyGetDashboardLSIncompleteRideQuery,
  useLazyGetDashboardRideMemberTableQuery,
  useLazyGetDashboardOnTimePerformanceTableQuery,
  useLazyGetDashboardRenderingServiceQuery,
  useLazyGetRideTableReportDataQuery,
  useLazyGetDashboardIncompleteRideTableQuery,
  useDownloadExcelMutation,
} = dashboardTablesApi;
